import { Controller } from "@hotwired/stimulus"
import { Tooltip } from "bootstrap"
import { disposeTooltip } from "../utils/bootstrap-tooltip.util"

export default class extends Controller {
  static targets = ["pill", "option"]
  static values = { current: String, saveUrl: String }

  #tooltips
  #previewHandler
  #mediaHandler

  connect() {
    this.#updateState(this.currentValue, false)
    this.#initTooltips()
    // Keep pill in sync when user previews theme from the settings form
    this.#previewHandler = ({ detail: { theme } }) => this.#syncPill(theme)
    document.addEventListener("theme:preview", this.#previewHandler)
    // Update bsTheme live when OS preference changes while "system" is active
    this.#mediaHandler = (e) => {
      if (this.currentValue === "system") {
        document.documentElement.dataset.bsTheme = e.matches ? "dark" : "light"
      }
    }
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", this.#mediaHandler)
  }

  disconnect() {
    this.#tooltips?.forEach((tooltip) => disposeTooltip(tooltip))
    document.removeEventListener("theme:preview", this.#previewHandler)
    window.matchMedia("(prefers-color-scheme: dark)").removeEventListener("change", this.#mediaHandler)
  }

  select({ currentTarget }) {
    const theme = currentTarget.dataset.value
    if (theme === this.currentValue) return
    const previousTheme = this.currentValue
    this.currentValue = theme
    this.#updateState(theme, true)
    this.#applyTheme(theme)
    this.#persist(theme, previousTheme)
  }

  #syncPill(theme) {
    // Visual-only update driven by the settings form preview — does not persist.
    // If the stored preference is "system", the nav segment keeps system selected;
    // the pill must not move to a resolved OS value ("light"/"dark").
    const pillTheme = this.currentValue === "system" && theme !== "system" ? "system" : theme
    const index = ["system", "light", "dark"].indexOf(pillTheme)
    if (this.hasPillTarget) this.pillTarget.dataset.index = Math.max(0, index)
    this.optionTargets.forEach((opt) => opt.classList.toggle("is-active", opt.dataset.value === pillTheme))
  }

  #updateState(theme, animate) {
    const index = ["system", "light", "dark"].indexOf(theme)
    if (this.hasPillTarget) {
      if (!animate) this.pillTarget.style.transition = "none"
      this.pillTarget.dataset.index = Math.max(0, index)
      if (!animate) {
        // Force reflow to apply position without animation, then restore transition
        this.pillTarget.getBoundingClientRect()
        this.pillTarget.style.transition = ""
      }
    }
    this.optionTargets.forEach((opt) => opt.classList.toggle("is-active", opt.dataset.value === theme))
  }

  #applyTheme(theme) {
    const html = document.documentElement
    html.dataset.theme = theme
    if (theme === "system") {
      html.dataset.bsTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
    } else {
      html.dataset.bsTheme = theme
    }
    // Sync settings form select if it's currently open on the page
    const settingsSelect = document.getElementById("user_interface_theme")
    if (settingsSelect) settingsSelect.value = theme
  }

  async #persist(theme, previousTheme) {
    const token = document.querySelector('[name="csrf-token"]')?.content
    if (!token || !this.saveUrlValue) return
    try {
      const response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          Accept: "application/json"
        },
        body: JSON.stringify({ interface_theme: theme })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
    } catch {
      // Revert to previous theme if the request fails
      this.currentValue = previousTheme
      this.#updateState(previousTheme, true)
      this.#applyTheme(previousTheme)
    }
  }

  #initTooltips() {
    this.#tooltips = this.optionTargets.map(
      (el) =>
        new Tooltip(el, {
          trigger: "hover",
          placement: "top"
        })
    )
  }
}
